import React, { useEffect } from 'react';
import { useSafeClose } from '../../hooks/useSafeClose';
import './modals.css';

/**
 * SharedModal Component
 * Standardized modal wrapper following the Apple-inspired design system.
 * 
 * @param {boolean} isOpen - Controls visibility
 * @param {function} onClose - Callback when modal should close
 * @param {string} title - Header title
 * @param {React.ReactNode} children - Modal body content
 * @param {React.ReactNode} footer - Optional footer content
 * @param {string} size - 'small', 'medium', 'large', 'xlarge'
 */
const SharedModal = ({ 
    isOpen, 
    onClose, 
    title, 
    children, 
    footer, 
    size = 'medium',
    bodyStyle = {}
}) => {
    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // Safe-close logic
    const { overlayRef, onMouseDown, onMouseUp } = useSafeClose(onClose);

    if (!isOpen) return null;

    return (
        <div 
            className="co-modal-overlay" 
            ref={overlayRef}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
        >
            <div 
                className={`co-modal-content ${size}`} 
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="co-modal-header">
                    <h2>{title}</h2>
                    <button 
                        className="co-modal-close-btn" 
                        onClick={onClose}
                        title="Schließen"
                    >
                        ✕
                    </button>
                </div>

                {/* Body */}
                <div className="co-modal-body" style={bodyStyle}>
                    {children}
                </div>

                {/* Footer (Optional) */}
                {footer && (
                    <div className="co-modal-footer">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SharedModal;
